// frontend/src/components/PowerAddModal.js
// Modal component for adding power data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * PowerAddModal Component
 * Provides a dedicated interface for adding power data to the flowsheet
 */
const PowerAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one power parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Power data saved successfully!');

        // Refresh vitals banner (in case power data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          onSave();
        }

        // Close modal and reset form
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save power data');
      }
    } catch (error) {
      console.error('Error saving power data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save power data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define power parameters based on parameters.md
  const powerFields = [
    { name: 'biometrics_power_jump_best', label: 'Vertical Jump - Best', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_power_jump_percent_max', label: 'Vertical Jump - Percent Max', units: '%', inputType: 'InputNumber', placeholder: '%', rules: [{ type: 'number', min: 0, max: 100, message: 'Percentage must be between 0 and 100' }] },
    { name: 'biometrics_power_jump_average_height', label: 'Vertical Jump - Average Height', units: 'cm', inputType: 'InputNumber', placeholder: 'cm', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_power_landing_impact', label: 'Landing Impact Force', units: 'N', inputType: 'InputNumber', placeholder: 'N', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
    { name: 'biometrics_power_power_watts', label: 'Power Output', units: 'watts', inputType: 'InputNumber', placeholder: 'watts', rules: [{ type: 'number', min: 0, message: 'Value cannot be negative' }] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Power Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#f5222d', borderColor: '#f5222d' }}
        >
          Save Power Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Power Entry"
          description="Enter power assessment results and explosive strength measurements for the selected date and time. These assessments help track muscular power and explosive strength changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="power_add_form">
        <Title level={5}>Power Assessment Parameters</Title>
        
        {/* Vertical Jump Tests */}
        <Divider orientation="left">Vertical Jump Tests</Divider>
        <Row gutter={16}>
          {powerFields.slice(0, 3).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Power Output Measurements */}
        <Divider orientation="left">Power Output Measurements</Divider>
        <Row gutter={16}>
          {powerFields.slice(3).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={`${field.label}${field.units ? ` (${field.units})` : ''}`}
                rules={field.rules}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  placeholder={field.placeholder}
                  min={field.rules?.[0]?.min}
                  max={field.rules?.[0]?.max}
                  precision={1}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">Test Instructions</Divider>
        <Alert
          message="Power Test Guidelines"
          description={
            <div>
              <p><strong>Vertical Jump Tests:</strong> Measure maximum vertical jump height from standing position. Record best attempt, average of multiple attempts, and percentage of maximum performance.</p>
              <p><strong>Landing Impact Force:</strong> Measure the force generated upon landing from jump tests using force plates or similar equipment.</p>
              <p><strong>Power Output:</strong> Measure mechanical power output during explosive movements, typically measured in watts using specialized equipment.</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default PowerAddModal;
